import React from 'react';
import Title from 'components/UI/Title';
import { observer, pricingCalculator as store } from 'store';
import { useTrans } from 'utils/localisation';
import { replaceKeyword } from 'utils';
import {
  minRateUs,
  maxRateUs,
  minRateMena,
  maxRateMena,
  minRateGb,
  maxRateGb,
  minRateEu,
  maxRateEu,
  minRateDach,
  maxRateDach,
  workingHours,
} from './constants';

import * as styles from './TagsLayout.module.scss';

function TagsLayout({ calculatorID, analyticsEvent }) {
  const t = useTrans(['common', 'pricingCalculator']);
  const { getPayload } = store;
  const payload = getPayload();
  const titleColor = 'white';

  const months = payload?.duration;
  const splitMonths = months?.split('-');
  const price = calculatePrice(calculatorID, payload, analyticsEvent);

  const priceRange = `${price?.currency}${price?.minPrice}K–${price?.maxPrice}K`;

  const priceRangeWithMonths = months
    ? months === '12+'
      ? `${price?.currency}${(price?.minPrice * 12).toLocaleString('fr')}K–${(
          price?.maxPrice * 18
        ).toLocaleString('fr')}K`
      : `${price?.currency}${(price?.minPrice * splitMonths[0]).toLocaleString('fr')}K–${(
          price?.maxPrice * splitMonths[1]
        ).toLocaleString('fr')}K`
    : '';

  if (analyticsEvent) {
    store.setPrice(priceRange);
  }

  const costHintText =
    calculatorID === 'price'
      ? replaceKeyword(
          replaceKeyword(t('teamAugmentationHint'), 'months', `${months} ${t('months')}`),
          'priceRange',
          priceRangeWithMonths,
        )
      : t('customSoftDevHint');

  return !analyticsEvent ? (
    <Title className={styles.tagsTitle} tag="h4" color={titleColor}>
      {t('popupPricePlaceholder')}
    </Title>
  ) : (
    <>
      <Title className={styles.tagsTitle} tag="h4" color={titleColor}>
        {t('approximateCost')}
      </Title>
      <div className={styles.wrapper}>
        <div className={styles.cost}>
          <p className={styles.costSum}>{priceRange}</p>
        </div>
        {costHintText && <div className={styles.costHint}>{costHintText}</div>}
      </div>
    </>
  );
}

export default observer(TagsLayout);

function calculatePrice(calculatorID, payload, analyticsEvent) {
  let minRate = 0;
  let maxRate = 0;
  let currency = '';

  switch (analyticsEvent) {
    case 'calculator-dach':
      minRate = minRateDach;
      maxRate = maxRateDach;
      currency = '€';
      break;
    case 'calculator-gb':
      minRate = minRateGb;
      maxRate = maxRateGb;
      currency = '€';
      break;
    case 'calculator-us':
      minRate = minRateUs;
      maxRate = maxRateUs;
      currency = '$';
      break;
    case 'calculator-mena':
      minRate = minRateMena;
      maxRate = maxRateMena;
      currency = '$';
      break;
    case 'calculator-eu':
    default:
      minRate = minRateEu;
      maxRate = maxRateEu;
      currency = '€';
      break;
  }

  let minPrice = 0;
  let maxPrice = 0;
  if (calculatorID === 'price' && payload['development-team']) {
    const specialistsCount = Object.values(payload['development-team']).reduce((acc, value) => {
      acc += value;
      return acc;
    }, 0);

    minPrice = Math.ceil((specialistsCount * minRate * workingHours) / 1000);
    maxPrice = Math.ceil((specialistsCount * maxRate * workingHours) / 1000);
  }

  if (calculatorID === 'price-custom-software' && payload.development_direction) {
    const direction = payload.development_direction;
    const multipleDirections = direction === 'Mobile and web' || direction === 'Mobile und Web';
    // base team count is 7. If user selected more then one direction then we add 3 team members for each direction except first one
    const specialistsCount = multipleDirections ? 10 : 7;

    minPrice = Math.ceil((specialistsCount * minRate * workingHours) / 1000);
    maxPrice = Math.ceil((specialistsCount * maxRate * workingHours) / 1000);
  }

  if (minPrice && maxPrice) {
    return {
      minPrice,
      maxPrice,
      currency,
    };
  }

  return null;
}
